import React, { useState } from 'react';
import styled from 'styled-components';

/* The playlist is embedded behind a facade: until someone actually asks for
   it, the page ships a poster and nothing else. Clicking swaps in the real
   iframe. Two reasons, and the second is the load-bearing one:

   1. The YouTube player is over a megabyte of script. This page's job is to
      be read, and almost nobody who lands here wants the video first.
   2. A cold iframe sets third-party cookies on arrival. Behind a click, the
      viewer opted in — and youtube-nocookie keeps even that minimal. */

const PLAYLIST_ID = 'PLFFj5IiyqKDI';

const EMBED_SRC =
  `https://www.youtube-nocookie.com/embed?listType=playlist` +
  `&list=${PLAYLIST_ID}&rel=0&modestbranding=1&autoplay=1`;

const Frame = styled.div`
  position: relative;
  margin-top: 2rem;
  /* Sized to the prose measure (Body is 62ch) so it reads as a figure set
     into the argument, not a hero banner interrupting it. */
  max-width: 40rem;
  aspect-ratio: 16 / 9;
  border: 1px solid var(--line);
  background: var(--sunken);
  overflow: hidden;

  /* aspect-ratio is recent enough that Gatsby 2's browser floor may miss it;
     the padding trick holds the box for anything that doesn't support it. */
  @supports not (aspect-ratio: 16 / 9) {
    height: 0;
    padding-bottom: 56.25%;
  }

  iframe {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border: 0;
    display: block;
  }
`;

const Facade = styled.button`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  padding: 0;
  border: 0;
  cursor: pointer;
  display: block;
  background-color: var(--old-glory-blue);
  background-image: url('/playlist-poster.png');
  background-size: cover;
  background-position: center;
  font: inherit;
  color: inherit;

  /* No scrim and no caption over the art. The chapter card already carries
     four typographic elements of its own; anything added here competes with
     them. The badge alone says "this plays." */

  &:focus-visible {
    outline: 2px solid var(--brass);
    outline-offset: -2px;
  }
`;

/* Not YouTube's red rounded-rectangle — that mark belongs to them and reads
   as an ad on this ground. A brass ring and a plain triangle. */
const PlayBadge = styled.span`
  position: absolute;
  z-index: 1;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 64px;
  height: 64px;
  border-radius: 50%;
  border: 2px solid var(--brass);
  background: rgba(4, 11, 23, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 160ms ease;

  ${Facade}:hover &,
  ${Facade}:focus-visible & {
    background: rgba(4, 11, 23, 0.85);
  }

  &::before {
    content: '';
    /* the triangle, nudged right so it sits optically centred in the ring */
    margin-left: 5px;
    border-style: solid;
    border-width: 10px 0 10px 17px;
    border-color: transparent transparent transparent var(--brass);
  }

  @media (max-width: 34rem) {
    width: 62px;
    height: 62px;

    &::before {
      margin-left: 5px;
      border-width: 10px 0 10px 17px;
    }
  }
`;

const PlaylistEmbed = () => {
  const [playing, setPlaying] = useState(false);

  return (
    <Frame>
      {playing ? (
        <iframe
          src={EMBED_SRC}
          title="Saxonberg MUD: Gamified Government for Digital Communities"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      ) : (
        <Facade
          type="button"
          onClick={() => setPlaying(true)}
          aria-label="Play the series, starting with Chapter 1: The Whole Machine, End to End"
        >
          <PlayBadge aria-hidden="true" />
        </Facade>
      )}
    </Frame>
  );
};

export default PlaylistEmbed;
